import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
// Firebase
import { db } from '../firebase';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { addDoc, collection, doc, query, getDocs, serverTimestamp, updateDoc, where } from 'firebase/firestore';
// Routers
import { useNavigate } from 'react-router-dom';

const SCHOOL_LOCATION = {
  name: 'Ubunifu College',
  address: '4th Floor, Paramount Plaza, Nairobi, Kenya',
  latitude: -1.2771472,
  longitude: 36.8214853,
};

const SCHOOL_RADIUS_METERS = 200;
const LOCATION_ACCURACY_TARGET_METERS = 60;
const LOCATION_MAX_ATTEMPTS = 3;

const requestFreshBrowserLocation = () => new Promise((resolve, reject) => {
  navigator.geolocation.getCurrentPosition(
    resolve,
    reject,
    {
      enableHighAccuracy: true,
      timeout: 12000,
      maximumAge: 0,
    }
  );
});

const getBestAvailableLocation = async () => {
  let bestPosition = null;
  let lastError = null;

  for (let attempt = 0; attempt < LOCATION_MAX_ATTEMPTS; attempt += 1) {
    try {
      const position = await requestFreshBrowserLocation();
      const accuracy = Number(position?.coords?.accuracy || Number.POSITIVE_INFINITY);

      if (!bestPosition || accuracy < Number(bestPosition?.coords?.accuracy || Number.POSITIVE_INFINITY)) {
        bestPosition = position;
      }

      if (accuracy <= LOCATION_ACCURACY_TARGET_METERS) {
        break;
      }
    } catch (error) {
      lastError = error;
    }
  }

  if (bestPosition) {
    return bestPosition;
  }

  throw lastError || new Error('Unable to determine location.');
};

const getDistanceInMeters = (fromLat, fromLng, toLat, toLng) => {
  const earthRadius = 6371000;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const deltaLat = toRadians(toLat - fromLat);
  const deltaLng = toRadians(toLng - fromLng);
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(toRadians(fromLat)) *
      Math.cos(toRadians(toLat)) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
};

function DarasaComp() {

  const currentMonth = new Date().toISOString().slice(0, 7); // Get the current month in YYYY-MM format
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserStatus, setCurrentUserStatus] = useState("");
  const [showSchoolPrompt, setShowSchoolPrompt] = useState(false);
  const [isCheckingLocation, setIsCheckingLocation] = useState(false);
  const [schoolCheckResult, setSchoolCheckResult] = useState(null);
  const [activeSchoolSession, setActiveSchoolSession] = useState(null);

  const auth = getAuth();
  let navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const uid = user.uid;
        const q = query(collection(db, "AttendaceReport"), where("userId", "==", uid));

        try {
          setIsLoading(true);
          const userQuery = query(collection(db, "users"), where("userId", "==", uid));
          const userSnapshot = await getDocs(userQuery);
          const userDoc = userSnapshot.docs[0]?.data?.();
          setCurrentUserStatus(userDoc?.userStatus || userDoc?.Status || "");

          const QuerySnapshot = await getDocs(q);

          let fetchedRows = [];
          QuerySnapshot.forEach((doc) => {
            const sessionData = doc.data();
            const startTime = sessionData.signInTime?.toDate?.() || sessionData.startTime?.toDate?.();
            const endTime = sessionData.signOutTime?.toDate?.() || sessionData.endTime?.toDate?.();

            if (!startTime) return;

            const dateKey = startTime.toISOString().slice(0, 10);
            const mode = sessionData.mode || sessionData.attendanceMode || (sessionData.roomlink ? "Online" : "Onsite");
            const status = sessionData.status || (endTime ? "Present" : "Ongoing");
            const sessionTitle = sessionData.sessionTitle || sessionData.className || "Class Session";

            fetchedRows.push({
              id: doc.id,
              dateKey,
              sessionTitle,
              mode,
              status,
              checkInTime: startTime,
              checkOutTime: endTime
            });
          });

          fetchedRows.sort((a, b) => b.checkInTime - a.checkInTime);
          setAttendanceRows(fetchedRows);
          const currentSchoolSession = fetchedRows.find((row) => (
            row.mode.toLowerCase() === "onsite" &&
            row.sessionTitle === "School Check-In" &&
            !row.checkOutTime
          ));
          setActiveSchoolSession(currentSchoolSession || null);
          setIsLoading(false);
        } catch (error) {
          console.error("Error fetching user data: ", error);
          setIsLoading(false);
        }
      } else {
        setCurrentUser(null);
        setCurrentUserStatus("");
        setActiveSchoolSession(null);
        navigate("/login");
        setIsLoading(false);
      }
    });

    return () => unsubscribe(); // Clean up the subscription
  }, [auth, navigate]);

  const filteredRows = useMemo(() => {
    return attendanceRows.filter((row) => row.dateKey.startsWith(selectedMonth));
  }, [attendanceRows, selectedMonth]);

  const totalSessions = filteredRows.length;
  const presentCount = filteredRows.filter((row) => row.status === "Present").length;
  const absentCount = filteredRows.filter((row) => row.status === "Absent").length;
  const onsiteCount = filteredRows.filter((row) => row.mode.toLowerCase() === "onsite").length;
  const onlineCount = filteredRows.filter((row) => row.mode.toLowerCase() === "online").length;

  const totalMinutes = filteredRows.reduce((sum, row) => {
    if (!row.checkOutTime) return sum;
    const diff = Math.max(0, row.checkOutTime - row.checkInTime);
    return sum + Math.round(diff / 60000);
  }, 0);

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const totalHoursLabel = totalMinutes ? `${hours}h ${minutes}m` : "0h";

  const getDurationLabel = (row) => {
    if (!row.checkOutTime) return "--";
    const diff = Math.max(0, row.checkOutTime - row.checkInTime);
    const mins = Math.round(diff / 60000);
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    if (hrs === 0) return `${rem}m`;
    return `${hrs}h ${rem}m`;
  };

  const getStatusClass = (status) => {
    const normalized = status?.toLowerCase() || "present";
    if (normalized.includes("late")) return "late";
    if (normalized.includes("absent")) return "absent";
    if (normalized.includes("ongoing")) return "ongoing";
    return "present";
  };

  const schoolMapUrl = `https://www.google.com/maps?q=${SCHOOL_LOCATION.latitude},${SCHOOL_LOCATION.longitude}&z=17&output=embed`;
  const canConfirmSchoolCheckIn = currentUserStatus.trim().toLowerCase() === "student";

  const closeSchoolPrompt = () => {
    if (isCheckingLocation) return;
    setShowSchoolPrompt(false);
  };

  const handleSchoolPresenceAnswer = async (isAtSchool) => {
    if (isAtSchool && !canConfirmSchoolCheckIn) {
      setSchoolCheckResult({
        kind: "error",
        title: "Check-in unavailable",
        message: "School sign-in is only available once your status becomes Student.",
      });
      setShowSchoolPrompt(false);
      return;
    }

    if (activeSchoolSession) {
      setSchoolCheckResult({
        kind: "duplicate",
        title: "Already checked in",
        message: "You already have an active school check-in. Please check out first.",
      });
      setShowSchoolPrompt(false);
      return;
    }

    if (!isAtSchool) {
      setSchoolCheckResult({
        kind: "not-at-school",
        title: "Check-in cancelled",
        message: "No problem. Only sign in once you are physically at school.",
      });
      setShowSchoolPrompt(false);
      return;
    }

    if (!navigator.geolocation) {
      setSchoolCheckResult({
        kind: "error",
        title: "Location unavailable",
        message: "Your browser does not support location access on this device.",
      });
      setShowSchoolPrompt(false);
      return;
    }

    setIsCheckingLocation(true);

    (async () => {
      try {
        const position = await getBestAvailableLocation();
        const { latitude, longitude } = position.coords;
        const accuracyInMeters = Math.round(position.coords.accuracy || 0);
        const distanceInMeters = Math.round(
          getDistanceInMeters(
            latitude,
            longitude,
            SCHOOL_LOCATION.latitude,
            SCHOOL_LOCATION.longitude
          )
        );

        const withinSchoolRadius = distanceInMeters <= SCHOOL_RADIUS_METERS;
        const todayKey = new Date().toISOString().slice(0, 10);

        if (!withinSchoolRadius) {
          setSchoolCheckResult({
            kind: "far",
            title: "You are far from the school",
            message: `Your latest location shows you about ${distanceInMeters}m from ${SCHOOL_LOCATION.name}. You must be within 200m of the campus to sign in. If you are already there, turn on precise location, move to an open area, and try again.`,
            distanceInMeters,
            accuracyInMeters,
          });
          return;
        }

        if (!currentUser) {
          setSchoolCheckResult({
            kind: "error",
            title: "Check-in failed",
            message: "We could not confirm your student account. Please refresh and try again.",
          });
          return;
        }

        const existingQuery = query(collection(db, "AttendaceReport"), where("userId", "==", currentUser.uid));
        const existingSnapshot = await getDocs(existingQuery);
        const alreadyCheckedInToday = existingSnapshot.docs.some((docSnap) => {
          const data = docSnap.data();
          const startTime = data.signInTime?.toDate?.() || data.startTime?.toDate?.();
          const endTime = data.signOutTime?.toDate?.() || data.endTime?.toDate?.();
          const mode = data.mode || data.attendanceMode || (data.roomlink ? "Online" : "Onsite");
          if (!startTime) return false;
          const dayKey = startTime.toISOString().slice(0, 10);
          return dayKey === todayKey && mode.toLowerCase() === "onsite" && data.sessionTitle === "School Check-In" && !!endTime;
        });

        if (alreadyCheckedInToday) {
          setSchoolCheckResult({
            kind: "duplicate",
            title: "Already signed in today",
            message: `You have already signed in at school today. Current distance: ${distanceInMeters}m from campus.`,
            distanceInMeters,
          });
          return;
        }

        const sessionRef = await addDoc(collection(db, "AttendaceReport"), {
          userId: currentUser.uid,
          username: currentUser.displayName || currentUser.email || "Student",
          signInTime: serverTimestamp(),
          sessionTitle: "School Check-In",
          mode: "Onsite",
          attendanceMode: "Onsite",
          status: "Ongoing",
          locationCheck: {
            latitude,
            longitude,
            schoolLatitude: SCHOOL_LOCATION.latitude,
            schoolLongitude: SCHOOL_LOCATION.longitude,
            distanceInMeters,
            accuracyInMeters,
            radiusInMeters: SCHOOL_RADIUS_METERS,
            schoolName: SCHOOL_LOCATION.name,
          },
        });

        setAttendanceRows((currentRows) => {
          const now = new Date();
          const newRow = {
            id: sessionRef.id,
            dateKey: now.toISOString().slice(0, 10),
            sessionTitle: "School Check-In",
            mode: "Onsite",
            status: "Ongoing",
            checkInTime: now,
            checkOutTime: null,
          };
          return [newRow, ...currentRows];
        });
        setActiveSchoolSession({
          id: sessionRef.id,
          dateKey: new Date().toISOString().slice(0, 10),
          sessionTitle: "School Check-In",
          mode: "Onsite",
          status: "Ongoing",
          checkInTime: new Date(),
          checkOutTime: null,
        });

        setSchoolCheckResult({
          kind: "success",
          title: "Checked in successfully",
          message: `Location confirmed at about ${distanceInMeters}m from ${SCHOOL_LOCATION.name}. Your on-site attendance is now active until you check out.`,
          distanceInMeters,
          accuracyInMeters,
        });
      } catch (error) {
        let message = "We could not get your location. Please allow location access and try again.";
        if (error.code === 1) {
          message = "Location permission was denied. Please allow access to confirm that you are at school.";
        } else if (error.code === 2) {
          message = "We could not determine your location accurately. Please try again in an open area with precise location enabled.";
        } else if (error.code === 3) {
          message = "Location request timed out. Please try again.";
        }

        console.error("Error recording school check-in:", error);
        setSchoolCheckResult({
          kind: "error",
          title: "Location check failed",
          message,
        });
      } finally {
        setIsCheckingLocation(false);
        setShowSchoolPrompt(false);
      }
    })();
  };

  const handleSchoolCheckout = async () => {
    if (!activeSchoolSession || !currentUser) {
      return;
    }

    setIsCheckingLocation(true);

    try {
      await updateDoc(doc(db, "AttendaceReport", activeSchoolSession.id), {
        signOutTime: serverTimestamp(),
        status: "Present",
      });

      const now = new Date();
      setAttendanceRows((currentRows) => currentRows.map((row) => (
        row.id === activeSchoolSession.id
          ? {
              ...row,
              status: "Present",
              checkOutTime: now,
            }
          : row
      )));
      setActiveSchoolSession(null);
      setSchoolCheckResult({
        kind: "success",
        title: "Checked out successfully",
        message: "Your school attendance session has been closed and recorded.",
      });
    } catch (error) {
      console.error("Error checking out from school:", error);
      setSchoolCheckResult({
        kind: "error",
        title: "Check-out failed",
        message: "We could not complete your school check-out right now. Please try again.",
      });
    } finally {
      setIsCheckingLocation(false);
    }
  };

  return (
    <div className="attendanceTracker">
      <div className="attendanceHeader">
        <div>
          <h5>Attendance Tracker</h5>
          <p>Monitor your online and on-site attendance, check-in times, and total learning hours.</p>
          <p className="attendanceHeaderNote">Virtual class attendance is logged automatically only when you join a live class from My Class Rooms.</p>
        </div>
      </div>

      <div className="attendanceCheckInPanel">
        <div className="attendanceCheckInInfo">
          <span className="attendanceCheckInEyebrow">On-site check-in</span>
          <h6>Confirm that you are physically at school</h6>
          <p>
            We verify your location within a strict 200m radius of {SCHOOL_LOCATION.name} before recording your on-site attendance.
          </p>
          <div className="attendanceCheckInActions">
            <button
              type="button"
              className="attendanceCheckInBtn"
              onClick={() => {
                if (activeSchoolSession) {
                  handleSchoolCheckout();
                  return;
                }
                setShowSchoolPrompt(true);
              }}
              disabled={isCheckingLocation}
            >
              {isCheckingLocation
                ? (activeSchoolSession ? "Checking out..." : "Checking location...")
                : (activeSchoolSession ? "Check Out From School" : "Sign In To School")}
            </button>
            <span className="attendanceCheckInHint">
              This action signs you in right now. The month selector below is only for browsing attendance history.
            </span>
          </div>
          {activeSchoolSession && (
            <div className="attendanceActiveSession">
              <strong>Currently checked in</strong>
              <span>Since {activeSchoolSession.checkInTime?.toLocaleTimeString() || "--"}</span>
            </div>
          )}
          {schoolCheckResult && (
            <div className={`attendanceCheckInResult attendanceCheckInResult--${schoolCheckResult.kind}`}>
              <strong>{schoolCheckResult.title}</strong>
              <p>{schoolCheckResult.message}</p>
            </div>
          )}
        </div>
        <div className="attendanceSchoolMap">
          <iframe
            title="Ubunifu College map"
            src={schoolMapUrl}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="attendanceSchoolAddress">
            <strong>{SCHOOL_LOCATION.name}</strong>
            <span>{SCHOOL_LOCATION.address}</span>
          </div>
        </div>
      </div>

      <div className="attendanceCards">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div className="attendanceCard attendanceCard--skeleton" key={`attendance-skeleton-${index}`}>
              <div className="skeleton-line skeleton-line--title" />
              <div className="skeleton-line" />
              <div className="skeleton-line" />
            </div>
          ))
        ) : (
          <>
            <div className="attendanceCard">
              <p>Total Sessions</p>
              <h3>{totalSessions}</h3>
              <span>{selectedMonth}</span>
            </div>
            <div className="attendanceCard">
              <p>Present</p>
              <h3>{presentCount}</h3>
              <span>Sessions attended</span>
            </div>
            <div className="attendanceCard">
              <p>Absent</p>
              <h3>{absentCount}</h3>
              <span>Missed sessions</span>
            </div>
            <div className="attendanceCard">
              <p>On-Site Sessions</p>
              <h3>{onsiteCount}</h3>
              <span>Campus sessions</span>
            </div>
            <div className="attendanceCard">
              <p>Online Sessions</p>
              <h3>{onlineCount}</h3>
              <span>Virtual sessions</span>
            </div>
            <div className="attendanceCard">
              <p>Total Hours</p>
              <h3>{totalHoursLabel}</h3>
              <span>Time in class</span>
            </div>
          </>
        )}
      </div>

      <div className="attendancePanel">
        <div className="attendancePanelHeader">
          <div>
            <h6>Attendance History</h6>
            <p className="attendancePanelSub">Browse your recorded sessions for any month.</p>
          </div>
          <div className="attendancePanelTools">
            <div className="attendanceFilters attendanceFilters--history">
              <label htmlFor="month">History month</label>
              <input
                type="month"
                id="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
            </div>
            <span>{isLoading ? "Loading..." : `${filteredRows.length} records`}</span>
          </div>
        </div>
        <div className="attendanceTableWrap">
          <table className="attendanceTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Session</th>
                <th>Mode</th>
                <th>Status</th>
                <th>Check-In</th>
                <th>Check-Out</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`attendance-row-skeleton-${index}`}>
                    <td><div className="skeleton-line" /></td>
                    <td><div className="skeleton-line" /></td>
                    <td><div className="skeleton-line" /></td>
                    <td><div className="skeleton-line" /></td>
                    <td><div className="skeleton-line" /></td>
                    <td><div className="skeleton-line" /></td>
                    <td><div className="skeleton-line" /></td>
                  </tr>
                ))
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="attendanceEmpty">No attendance records found for this history month yet.</td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.dateKey}</td>
                    <td>{row.sessionTitle}</td>
                    <td>
                      <span className={`modePill modePill--${row.mode.toLowerCase()}`}>
                        {row.mode}
                      </span>
                    </td>
                    <td>
                      <span className={`statusPill statusPill--${getStatusClass(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>{row.checkInTime?.toLocaleTimeString() || "--"}</td>
                    <td>{row.checkOutTime?.toLocaleTimeString() || "--"}</td>
                    <td>{getDurationLabel(row)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showSchoolPrompt} onHide={closeSchoolPrompt} centered>
        <Modal.Header closeButton={!isCheckingLocation}>
          <Modal.Title>School check-in</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="attendancePromptText">
            Are you currently at school? If you confirm, we will request a fresh location reading from your browser and verify that you are within 200m of the campus before signing you in.
          </p>
          {!canConfirmSchoolCheckIn && (
            <p className="attendancePromptNote">
              You can view this check-in flow, but only users with Student status can confirm school sign-in.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => handleSchoolPresenceAnswer(false)} disabled={isCheckingLocation}>
            Not yet
          </Button>
          <Button variant="primary" onClick={() => handleSchoolPresenceAnswer(true)} disabled={isCheckingLocation || !canConfirmSchoolCheckIn}>
            {isCheckingLocation ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="attendancePromptSpinnerText"> Checking...</span>
              </>
            ) : (
              "Yes, I am at school"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default DarasaComp;
